import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Users, Plus, Search, MessageSquare, Linkedin, Instagram, Loader2, ExternalLink, CheckCircle2, XCircle, Activity, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    active: { label: "Active", icon: Activity, cls: "bg-signal/10 text-signal border-signal/30" },
    booked: { label: "Booked", icon: CheckCircle2, cls: "bg-green-500/10 text-green-400 border-green-500/30" },
    lost: { label: "Lost", icon: XCircle, cls: "bg-zinc-800 text-zinc-500 border-zinc-700" },
};

const METHOD_LABELS = {
    nepq: "NEPQ", spin: "SPIN", challenger: "Challenger", sandler: "Sandler",
    meddic: "MEDDIC", gap: "Gap Selling", consultative: "Consultative", straight_line: "Straight Line",
};

const TONES = ["consultative", "direct", "casual", "expert", "playful"];
const INDUSTRIES = ["saas", "agency", "ecommerce", "fitness_coaching", "real_estate", "finance", "medical_coaches", "education", "real_estate_investing", "insurance", "home_services"];
const METHODS = Object.keys(METHOD_LABELS);

export default function Clients() {
    const { authHeaders } = useAuth();
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        name: "", prospect_company: "", prospect_url: "",
        platform: "linkedin", tone: "consultative", industry: "saas",
        methodology: "nepq", goal: "book_meeting", seed_text: "",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (q) params.q = q;
            const res = await axios.get(`${API}/clients`, { headers: authHeaders(), params });
            setThreads(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load clients");
        } finally { setLoading(false); }
    }, [authHeaders, statusFilter, q]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Give the client a name."); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/clients`, form, { headers: authHeaders() });
            toast.success(`Created thread for ${res.data.name}`);
            setModalOpen(false);
            setForm({ name: "", prospect_company: "", prospect_url: "", platform: "linkedin", tone: "consultative", industry: "saas", methodology: "nepq", goal: "book_meeting", seed_text: "" });
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create thread");
        } finally { setCreating(false); }
    };

    return (
        <div data-testid="clients-page">
            <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ CLIENTS</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">Your live conversations.</h1>
                    <p className="text-zinc-400 mt-2 max-w-2xl">
                        One thread per prospect. Your team can drop in new messages over time, get AI replies in the right methodology, and keep the full history in one place.
                    </p>
                </div>
                <button
                    onClick={() => setModalOpen(true)}
                    data-testid="new-client-btn"
                    className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-5 py-3 rounded-md font-semibold transition-colors"
                >
                    <Plus className="w-4 h-4" /> New client thread
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="flex items-center gap-1.5 border border-zinc-900 rounded-md bg-[#0c0c0c] p-1">
                    {["all", "active", "booked", "lost"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            data-testid={`clients-filter-${s}`}
                            className={`px-3 py-1.5 rounded text-xs font-mono uppercase tracking-widest transition-colors ${statusFilter === s ? "bg-signal text-white" : "text-zinc-400 hover:text-white"}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by client or company…"
                        data-testid="clients-search"
                        className="w-full bg-[#0c0c0c] border border-zinc-900 rounded-md text-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                    />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-zinc-500" data-testid="clients-loading">Loading threads…</div>
            ) : threads.length === 0 ? (
                <div className="border border-dashed border-zinc-900 p-16 text-center text-zinc-500" data-testid="clients-empty">
                    <Users className="w-10 h-10 mx-auto mb-4 text-zinc-700" strokeWidth={1.2} />
                    <div className="mb-1">No client threads yet.</div>
                    <div className="text-xs">Click "New client thread" to start one.</div>
                </div>
            ) : (
                <div className="space-y-3" data-testid="clients-list">
                    {threads.map((t) => {
                        const status = STATUS_META[t.status] || STATUS_META.active;
                        const StatusIcon = status.icon;
                        const Platform = t.platform === "instagram" ? Instagram : Linkedin;
                        return (
                            <Link
                                key={t.id}
                                to={`/app/clients/${t.id}`}
                                data-testid={`client-row-${t.id}`}
                                className="block border border-zinc-900 bg-[#0c0c0c] rounded-md p-5 hover:border-signal/50 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                                            <h2 className="font-heading font-bold text-lg text-white truncate">{t.name}</h2>
                                            {t.prospect_company && <span className="text-xs text-zinc-500 font-mono">· {t.prospect_company}</span>}
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${status.cls}`}>
                                                <StatusIcon className="w-3 h-3" /> {status.label}
                                            </span>
                                        </div>
                                        <div className="text-sm text-zinc-300 truncate mb-2">
                                            {t.last_turn_preview || <span className="text-zinc-600 italic">No messages yet</span>}
                                        </div>
                                        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                                            <span className="inline-flex items-center gap-1"><Platform className="w-3 h-3" /> {t.platform}</span>
                                            <span>·</span>
                                            <span className="text-signal">{METHOD_LABELS[t.methodology]}</span>
                                            <span>·</span>
                                            <span>{t.tone}</span>
                                            <span>·</span>
                                            <span>{t.industry}</span>
                                            <span>·</span>
                                            <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.turn_count}</span>
                                        </div>
                                    </div>
                                    <div className="text-[10px] font-mono text-zinc-600 text-right shrink-0">
                                        {t.last_turn_at ? new Date(t.last_turn_at).toLocaleDateString() : "—"}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* New client modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
                    <div className="bg-[#0a0a0a] border border-zinc-800 rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-client-modal">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-heading font-bold text-2xl">New client thread</h2>
                            <button onClick={() => setModalOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={create} className="space-y-4">
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Client name *</label>
                                <input
                                    required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                                    placeholder="John Doe"
                                    data-testid="new-client-name"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Company</label>
                                    <input
                                        value={form.prospect_company} onChange={(e) => setForm({...form, prospect_company: e.target.value})}
                                        placeholder="Acme Corp"
                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Profile URL</label>
                                    <input
                                        value={form.prospect_url} onChange={(e) => setForm({...form, prospect_url: e.target.value})}
                                        placeholder="linkedin.com/in/..."
                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Platform</label>
                                    <select value={form.platform} onChange={(e) => setForm({...form, platform: e.target.value})}
                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal">
                                        <option value="linkedin">LinkedIn</option>
                                        <option value="instagram">Instagram</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Tone</label>
                                    <select value={form.tone} onChange={(e) => setForm({...form, tone: e.target.value})}
                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal">
                                        {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Industry</label>
                                    <select value={form.industry} onChange={(e) => setForm({...form, industry: e.target.value})}
                                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal">
                                        {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Sales methodology</label>
                                <select value={form.methodology} onChange={(e) => setForm({...form, methodology: e.target.value})}
                                    data-testid="new-client-methodology"
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal">
                                    {METHODS.map(m => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
                                </select>
                                <p className="text-[10px] text-zinc-600 mt-1">Drives the style of every AI reply in this thread. Can be changed later.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">First message from them (optional)</label>
                                <textarea
                                    value={form.seed_text} onChange={(e) => setForm({...form, seed_text: e.target.value})}
                                    rows={3}
                                    placeholder="Paste their first DM or comment if you have it."
                                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal resize-none"
                                />
                            </div>
                            <button type="submit" disabled={creating} data-testid="new-client-submit"
                                className="w-full bg-signal hover:bg-signal-hover text-white px-5 py-3 rounded-md font-semibold transition-colors disabled:opacity-60">
                                {creating ? "Creating…" : "Create thread"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
